import { forwardRef, useId, useState, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react'
import { Eye, EyeOff } from 'lucide-react'

import { cn } from '@/lib/cn'

const control =
  'w-full rounded-xl border border-line bg-page px-3.5 text-[15px] text-ink transition placeholder:text-subtle ' +
  'focus:border-line-strong focus:outline-none disabled:cursor-not-allowed disabled:opacity-60'

export function Field({
  label,
  hint,
  error,
  required,
  children,
  htmlFor,
  className,
}: {
  label: string
  hint?: ReactNode
  error?: string
  required?: boolean
  children: ReactNode
  htmlFor?: string
  className?: string
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <label htmlFor={htmlFor} className="flex items-center gap-1 text-sm font-semibold text-ink">
        {label}
        {required ? (
          <span className="text-danger" aria-hidden>
            *
          </span>
        ) : null}
      </label>
      {children}
      {/* Galat menggantikan petunjuk, bukan menumpuk di bawahnya, supaya
          pesan yang perlu dibaca tidak tenggelam. */}
      {error ? (
        <p className="text-sm font-medium text-danger" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="text-sm text-muted">{hint}</p>
      ) : null}
    </div>
  )
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  hint?: ReactNode
  error?: string
  prefix?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, prefix, className, id, required, type, ...props },
  ref,
) {
  const generatedId = useId()
  const inputId = id ?? generatedId

  // Kolom kata sandi mendapat tombol pengungkap. Di papan ketik ponsel,
  // mengetik kata sandi tanpa dapat memeriksanya adalah sumber kegagalan
  // masuk yang paling sering — dan pengguna tidak pernah tahu huruf mana
  // yang salah.
  const isPassword = type === 'password'
  const [revealed, setRevealed] = useState(false)
  const effectiveType = isPassword && revealed ? 'text' : type

  const field = (
    <div className="relative">
      {prefix ? (
        <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-sm text-muted">
          {prefix}
        </span>
      ) : null}
      <input
        ref={ref}
        id={inputId}
        type={effectiveType}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${inputId}-error` : undefined}
        className={cn(
          control,
          'h-11',
          prefix && 'pl-11',
          isPassword && 'pr-12',
          error && 'border-danger focus:border-danger focus:ring-danger/25',
          className,
        )}
        {...props}
      />
      {isPassword ? (
        <button
          type="button"
          // tabIndex -1 supaya Tab dari kolom kata sandi langsung menuju
          // tombol kirim, bukan mampir ke tombol hiasan ini.
          tabIndex={-1}
          onClick={() => setRevealed((current) => !current)}
          aria-label={revealed ? 'Sembunyikan kata sandi' : 'Tampilkan kata sandi'}
          aria-pressed={revealed}
          className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-subtle transition hover:text-ink"
        >
          {revealed ? <EyeOff className="size-4.5" /> : <Eye className="size-4.5" />}
        </button>
      ) : null}
    </div>
  )

  if (!label) return field

  return (
    <Field label={label} hint={hint} error={error} required={required} htmlFor={inputId}>
      {field}
    </Field>
  )
})

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  hint?: ReactNode
  error?: string
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, className, id, required, rows = 4, ...props },
  ref,
) {
  const generatedId = useId()
  const textareaId = id ?? generatedId

  const field = (
    <textarea
      ref={ref}
      id={textareaId}
      rows={rows}
      required={required}
      aria-invalid={error ? true : undefined}
      className={cn(control, 'py-2.5 leading-relaxed', error && 'border-danger focus:border-danger focus:ring-danger/25', className)}
      {...props}
    />
  )

  if (!label) return field

  return (
    <Field label={label} hint={hint} error={error} required={required} htmlFor={textareaId}>
      {field}
    </Field>
  )
})

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  hint?: ReactNode
  error?: string
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, className, id, required, children, ...props },
  ref,
) {
  const generatedId = useId()
  const selectId = id ?? generatedId

  const field = (
    <select
      ref={ref}
      id={selectId}
      required={required}
      aria-invalid={error ? true : undefined}
      className={cn(control, 'h-11 appearance-none bg-[length:16px] pr-9', error && 'border-danger', className)}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='none' stroke='%23858ca3' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m4 6 4 4 4-4'/%3E%3C/svg%3E\")",
        backgroundPosition: 'right 0.75rem center',
        backgroundRepeat: 'no-repeat',
      }}
      {...props}
    >
      {children}
    </select>
  )

  if (!label) return field

  return (
    <Field label={label} hint={hint} error={error} required={required} htmlFor={selectId}>
      {field}
    </Field>
  )
})

/** Checkbox dengan label yang seluruh areanya dapat diklik. */
export function Checkbox({
  label,
  description,
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string; description?: string }) {
  const generatedId = useId()
  const id = props.id ?? generatedId

  return (
    <label
      htmlFor={id}
      className={cn(
        'flex cursor-pointer items-start gap-3 rounded-xl border border-line bg-raised p-3.5 transition hover:border-brand/50',
        className,
      )}
    >
      <input
        id={id}
        type="checkbox"
        className="mt-0.5 size-4 shrink-0 accent-[var(--brand)]"
        {...props}
      />
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-ink">{label}</span>
        {description ? <span className="mt-0.5 block text-sm text-muted">{description}</span> : null}
      </span>
    </label>
  )
}
